import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { IpadFrame } from './components/snak/IpadFrame';
import { StudyDesignOverlay } from './components/snak/StudyDesignOverlay';
import { ClusterCompareOverlay } from './components/snak/ClusterCompareOverlay';
import { ClusterLightbox } from './components/snak/ClusterLightbox';
import { PermissionPage } from './components/snak/pages/PermissionPage';
import { ExpertQuestionPage } from './components/snak/pages/ExpertQuestionPage';
import { StudyBridgePage } from './components/snak/pages/StudyBridgePage';
import { ClusterRevealPage } from './components/snak/pages/ClusterRevealPage';
import { ReflectionPage } from './components/snak/pages/ReflectionPage';
import { EvidencePage } from './components/snak/pages/EvidencePage';
import { RelevancePage } from './components/snak/pages/RelevancePage';
import { ChooseDepthPage } from './components/snak/pages/ChooseDepthPage';
import { EndPage } from './components/snak/pages/EndPage';
import { chapters, chooseDepth, clusterReveal, evidence, expertQuestion, permission, reflection, relevance, repPrompts, snakReference, studyBridge } from './data/snak';
import type { Cluster } from './types/teaser';
import type { DepthChoice } from './types/snak';
const EASE = [0.23, 1, 0.32, 1] as const;
const PAGES = ['permission', 'question', 'bridge', 'reveal', 'reflection', 'evidence', 'relevance', 'depth', 'end'] as const;
type PageId = (typeof PAGES)[number];
interface AppProps {
  /** Show live NOK delegate responses after the reflection answer. */
  showPeerData?: boolean;
  /** Show the rep's verbal prompts on screen by default. */
  showRepPrompts?: boolean;
}
export function App({
  showPeerData = true,
  showRepPrompts = true
}: AppProps) {
  const [step, setStep] = useState(0);
  const [promptsVisible, setPromptsVisible] = useState(showRepPrompts);
  const [studyOpen, setStudyOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [zoomed, setZoomed] = useState<Cluster | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | null>>({
    question: null,
    reflection: null,
    relevance: null
  });
  const page: PageId = PAGES[step];
  const goTo = (id: PageId) => setStep(PAGES.indexOf(id));
  const next = () => setStep(s => Math.min(s + 1, PAGES.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));
  const answer = (key: string) => (id: string) => setAnswers(prev => ({
    ...prev,
    [key]: prev[key] === null ? id : prev[key]
  }));
  const restart = () => {
    setAnswers({
      question: null,
      reflection: null,
      relevance: null
    });
    setStep(0);
  };
  const onDepth = (choice: DepthChoice) => {
    if (choice === 'clusters') setCompareOpen(true);else if (choice === 'study') setStudyOpen(true);else goTo('end');
  };
  const nav = (() => {
    switch (page) {
      case 'permission':
        return {
          onNext: next,
          nextLabel: permission.cta
        };
      case 'question':
        return {
          onBack: back,
          onNext: next,
          nextLabel: expertQuestion.cta,
          nextDisabled: answers.question === null,
          secondary: {
            label: 'Skip',
            onClick: next
          }
        };
      case 'bridge':
        return {
          onBack: back,
          onNext: next,
          nextLabel: studyBridge.cta,
          footnote: snakReference
        };
      case 'reveal':
        return {
          onBack: back,
          onNext: next,
          nextLabel: clusterReveal.cta,
          footnote: snakReference
        };
      case 'reflection':
        return {
          onBack: back,
          onNext: next,
          nextLabel: reflection.cta,
          nextDisabled: answers.reflection === null,
          secondary: {
            label: 'Skip',
            onClick: next
          }
        };
      case 'evidence':
        return {
          onBack: back,
          onNext: next,
          nextLabel: evidence.cta,
          footnote: snakReference
        };
      case 'relevance':
        return {
          onBack: back,
          onNext: next,
          nextLabel: relevance.cta,
          nextDisabled: answers.relevance === null,
          secondary: {
            label: 'Skip',
            onClick: next
          }
        };
      case 'depth':
        return {
          onBack: back,
          footnote: chooseDepth.support
        };
      case 'end':
        return {
          onBack: back,
          footnote: snakReference
        };
      default:
        return {
          onBack: back,
          onNext: next
        };
    }
  })();
  return <div className="min-h-full w-full bg-mist font-sans" data-id="element-0" data-mp="c9kc215z6j0z">
      <IpadFrame step={step} total={PAGES.length} chapter={chapters[page]} prompt={repPrompts[page]} promptsVisible={promptsVisible} onTogglePrompts={() => setPromptsVisible(v => !v)} onOpenStudy={() => setStudyOpen(true)} {...nav} data-id="element-1" data-mp="19vrhfda3sfee">
        <AnimatePresence mode="wait" data-id="element-2" data-mp="vwrv4ca2ccle">
          <motion.div key={page} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="flex flex-1 flex-col" data-id="element-3" data-mp="vvp29yt6k02u">
            {page === 'permission' && <PermissionPage data-id="element-4" data-mp="1w69p040kv7s7" />}
            {page === 'question' && <ExpertQuestionPage selected={answers.question} onSelect={answer('question')} data-id="element-5" data-mp="2071zt1p3lkql" />}
            {page === 'bridge' && <StudyBridgePage data-id="element-6" data-mp="253sd5h9iq28b" />}
            {page === 'reveal' && <ClusterRevealPage onEnlarge={setZoomed} data-id="element-7" data-mp="29wd6yisfcfbu" />}
            {page === 'reflection' && <ReflectionPage selected={answers.reflection} onSelect={answer('reflection')} showPeerData={showPeerData} data-id="element-8" data-mp="2evfzx4v4qv3g" />}
            {page === 'evidence' && <EvidencePage onOpenStudy={() => setStudyOpen(true)} data-id="element-9" data-mp="2nwntxaqm59rq" />}
            {page === 'relevance' && <RelevancePage selected={answers.relevance} onSelect={answer('relevance')} data-id="element-10" data-mp="2ste8tuqolzbk" />}
            {page === 'depth' && <ChooseDepthPage onChoose={onDepth} data-id="element-11" data-mp="tjocrd6l47ic" />}
            {page === 'end' && <EndPage onRestart={restart} data-id="element-12" data-mp="yhg3w4zenplh" />}
          </motion.div>
        </AnimatePresence>
      </IpadFrame>

      <AnimatePresence data-id="element-13" data-mp="21s3xd3ubuqmm">
        {zoomed && <ClusterLightbox cluster={zoomed} onClose={() => setZoomed(null)} data-id="element-14" data-mp="1wshae2op5w0m" />}
      </AnimatePresence>
      <AnimatePresence data-id="element-15" data-mp="2as8tuixzg4m0">
        {compareOpen && <ClusterCompareOverlay onClose={() => setCompareOpen(false)} data-id="element-16" data-mp="2atbislbqvwza" />}
      </AnimatePresence>
      <AnimatePresence data-id="element-17" data-mp="25s4cyrt5x7v9">
        {studyOpen && <StudyDesignOverlay onClose={() => setStudyOpen(false)} data-id="element-18" data-mp="25va2u99m8ds1" />}
      </AnimatePresence>
    </div>;
}